import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMediaQuery } from 'react-responsive'
import { CloseOutlined, MenuOutlined as BurgerMenu } from '@ant-design/icons'
import { Menu } from 'antd'
import MeshpolLogo from 'assets/images/mesh-logo-square.svg'
import { Link } from 'gatsby'
import { useAuth } from 'store/authStore/authStore'

import ChangePageContrastButton from 'components/atoms/ChangePageContrastButton/ChangePageContrastButton'
import LoginDropdown from 'components/atoms/LoginDropdown/LoginDropdown'
import ChangeFontSizeButtons from 'components/molecules/ChangeFontSizeButtons/ChangeFontSizeButtons'

import SelectLanguage from '../../atoms/SelectLanguage/SelectLanguage'

import NavUserInfo from './../../molecules/NavUserInfo/NavUserInfo'
import AuthGate from './../../utilityComponents/AuthGate/AuthGate'
import { navigationItems } from './NavigationItems'

import {
    CloseIconWrapper,
    FlexWrapper,
    InnerWrapper,
    MenuIconWrapper,
    NavBar,
    NavLink,
    SideNav,
    StyledList,
    StyledLogo,
} from './Navigation.styles'

const Navigation = () => {
    const { t } = useTranslation()
    const [isActive, setIsActive] = useState(false)
    const [isBurgerVisible, setIsBurgerVisible] = useState(true)
    const [{ isAuthenticated, user }] = useAuth()

    const toggleSideNav = () => {
        setIsActive((current) => !current)
        setIsBurgerVisible((current) => !current)
    }

    const isSmallMobileView = useMediaQuery({ query: '(max-width: 1000px)' })
    const isExtraSmallMobileView = useMediaQuery({
        query: '(max-width: 650px)',
    })

    useEffect(() => {
        setIsActive(false)
        setIsBurgerVisible(true)
    }, [isSmallMobileView, isExtraSmallMobileView])

    const items = useMemo(() => {
        return navigationItems
            ?.sort((a, b) => a.key - b.key)
            ?.map((item) => {
                const hasAccess =
                    !item.authOnly ||
                    (isAuthenticated &&
                        user.roles.some(
                            (allowedRole) =>
                                item?.roles?.indexOf(allowedRole) !== -1
                        ))

                if (!hasAccess) return null

                const label = (
                    <li key={item.path}>
                        <NavLink onClick={toggleSideNav} to={item.path}>
                            {t(`${item.i18nKey}`)}
                        </NavLink>
                    </li>
                )

                return { label, key: item.path }
            })
    }, [navigationItems, isAuthenticated, user.roles])

    return (
        <NavBar>
            <Link to="/">
                <StyledLogo src={MeshpolLogo} alt="Meshpol logo" />
            </Link>
            {!isSmallMobileView && (
                <Menu
                    mode="horizontal"
                    items={items}
                    selectable={false}
                    style={{
                        border: 'none',
                        minWidth: 0,
                        flex: 'auto',
                    }}
                />
            )}

            <InnerWrapper>
                <AuthGate fallback={<LoginDropdown />}>
                    <NavUserInfo />
                </AuthGate>
                {!isExtraSmallMobileView && (
                    <StyledList>
                        <ChangeFontSizeButtons />
                        <ChangePageContrastButton />
                    </StyledList>
                )}
                <SelectLanguage />
                {isSmallMobileView && (
                    <>
                        <MenuIconWrapper
                            onClick={toggleSideNav}
                            aria-label="menu"
                        >
                            <BurgerMenu
                                className={isBurgerVisible ? '' : 'hidden'}
                            />
                        </MenuIconWrapper>
                        <SideNav
                            className={`wcag-compatible ${
                                isActive ? 'visible' : ''
                            }`}
                        >
                            {isExtraSmallMobileView && (
                                <FlexWrapper>
                                    <ChangeFontSizeButtons />
                                    <ChangePageContrastButton />
                                </FlexWrapper>
                            )}
                            <CloseIconWrapper
                                onClick={toggleSideNav}
                                aria-label={t('close')}
                            >
                                <CloseOutlined />
                            </CloseIconWrapper>
                            <Menu
                                mode="vertical"
                                items={items}
                                selectable={false}
                                style={{
                                    border: 'none',
                                    maxWidth: '500px',
                                }}
                            />
                        </SideNav>
                    </>
                )}
            </InnerWrapper>
        </NavBar>
    )
}

export default Navigation
